import { Extras } from "@sentry/types";
import { useEffect, useRef, useState } from "react";
import {
  type NavigateOptions,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { ROUTE_SLUGS } from "src/config/navigation/routes";
import { ROUTES } from "../config";
import { Document } from "../stores/models";
import {
  captureSentryException,
  fillRouteWithSlugs,
  findDocInstancesFromTemplate,
  getLatestDocumentVersion,
  hasDocumentRevision,
  isDraft,
} from "../utils";
import {
  useCreateDocumentMutation,
  useCreateDocumentVersionMutation,
} from "./mutations";
import { getDocumentsAsync } from "./queries";

const throwNavigationError = (msg: string, extras?: Extras) => {
  captureSentryException(msg, extras);
  throw new Error(msg);
};

/**
 * Get refreshed document and documents
 */
export const getFullDocumentAndVersions = async (
  orgId: string,
  deviceId: string,
  templateKey: string,
  documentId?: string
) => {
  const documents = await getDocumentsAsync(orgId, deviceId);
  const matchingDocuments = findDocInstancesFromTemplate(
    documents,
    templateKey
  );
  const document =
    documentId === undefined
      ? matchingDocuments[0]
      : documents.find((doc) => doc.id === documentId);
  return { documents, document, matchingDocuments };
};

// Shared state to track if any document operation is in progress
let globalPendingState = false;

// Use a Set to store listeners
const globalListeners = new Set<(pending: boolean) => void>();

/**
 * Updates the global pending state and notifies all listeners
 * This ensures all components using useDocEngine stay in sync
 */
const setGlobalPending = (pending: boolean) => {
  globalPendingState = pending;
  globalListeners.forEach((listener) => {
    listener(pending);
  });
};

type QMSDocumentSearchParams = URLSearchParams | Record<string, string>;

type NavigateToQMSDocumentOptions = {
  documentId: string;
  versionId?: string;
  tiptapVersion?: number | null;
  searchParams?: QMSDocumentSearchParams;
  state?: NavigateOptions["state"];
};

const appendSearchParams = (
  searchParams: URLSearchParams,
  params: QMSDocumentSearchParams | undefined
) => {
  if (!params) {
    return;
  }

  if (params instanceof URLSearchParams) {
    params.forEach((value, key) => {
      if (value) {
        searchParams.set(key, value);
      }
    });
    return;
  }

  Object.entries(params).forEach(([key, value]) => {
    if (value) {
      searchParams.set(key, value);
    }
  });
};

export const useDocEngine = (deviceId: string) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { pathname: currentPath } = location;
  const { orgId = "" } = useParams();

  const [pending, setPending] = useState(globalPendingState);
  const pendingNavigationTargetRef = useRef<string | null>(null);
  const lastLocationKeyRef = useRef(location.key);

  useEffect(() => {
    globalListeners.add(setPending);
    return () => {
      globalListeners.delete(setPending);
    };
  }, []);

  useEffect(() => {
    if (!pending) {
      lastLocationKeyRef.current = location.key;
      return;
    }

    const currentTarget = `${location.pathname}${location.search}`;
    const pendingTarget = pendingNavigationTargetRef.current;
    const locationChanged = location.key !== lastLocationKeyRef.current;

    if ((pendingTarget && pendingTarget === currentTarget) || locationChanged) {
      pendingNavigationTargetRef.current = null;
      setGlobalPending(false);
    }

    lastLocationKeyRef.current = location.key;
  }, [location.key, location.pathname, location.search, pending]);

  const createDocumentMutation = useCreateDocumentMutation();
  const createDocumentVersionMutation = useCreateDocumentVersionMutation(
    orgId,
    deviceId
  );

  const _createDocument = (templateKey: string) =>
    createDocumentMutation.mutateAsync({
      orgId,
      body: {
        templateKey,
        deviceId,
      },
    });

  /**
   * Get an existing doc, or dynamically create one (usually on first navigation).
   */
  const _resolveNavigationDocument = async (
    existingDocumentId: string | undefined,
    templateKey: string,
    createNewMultiInstanceDoc: boolean
  ) => {
    const { document: existingDocument } = await getFullDocumentAndVersions(
      orgId,
      deviceId,
      templateKey,
      existingDocumentId
    );
    let document = existingDocument;

    if (existingDocumentId && !document) {
      throwNavigationError(
        "Cannot navigate to a documentId that doesn't exist",
        { existingDocumentId }
      );
    }

    const createdDocument = !document || createNewMultiInstanceDoc;
    if (createdDocument) {
      document = await _createDocument(templateKey);
    }

    if (!document) {
      return throwNavigationError("Client Error: Document not returned.");
    }

    return { createdDocument, document };
  };

  const _getOrCreateDocument = async (
    existingDocumentId: string | undefined,
    templateKey: string,
    createNewMultiInstanceDoc: boolean
  ) =>
    (
      await _resolveNavigationDocument(
        existingDocumentId,
        templateKey,
        createNewMultiInstanceDoc
      )
    ).document;

  const navigateWithPending = (
    to: string | { pathname: string; search?: string },
    options?: NavigateOptions
  ) => {
    if (typeof to === "string") {
      pendingNavigationTargetRef.current = to;
    } else {
      pendingNavigationTargetRef.current = `${to.pathname}${to.search ?? ""}`;
    }
    navigate(to, { ...options, flushSync: true });
  };

  const navigateToQMSDocument = ({
    documentId,
    searchParams,
    state,
    tiptapVersion,
    versionId,
  }: NavigateToQMSDocumentOptions) => {
    const qmsUrl = fillRouteWithSlugs(ROUTES.QMS_OPEN_DOC, {
      [ROUTE_SLUGS.ORG_ID]: orgId,
      [ROUTE_SLUGS.DEVICE_ID]: deviceId,
      [ROUTE_SLUGS.DOC_ID]: documentId,
    });
    const nextSearchParams = new URLSearchParams();

    if (versionId) {
      nextSearchParams.set("v", versionId);
    }

    if (typeof tiptapVersion === "number") {
      nextSearchParams.set("tiptapVersion", String(tiptapVersion));
    }

    appendSearchParams(nextSearchParams, searchParams);

    const search = nextSearchParams.toString();
    const navigateOptions = state === undefined ? undefined : { state };

    return navigateWithPending(
      search ? `${qmsUrl}?${search}` : qmsUrl,
      navigateOptions
    );
  };

  const navigateToQMS = (
    docId: string,
    versionId?: string,
    tiptapVersion?: number | null
  ) => navigateToQMSDocument({ documentId: docId, tiptapVersion, versionId });

  /**
   * Navigate to the correct view for a document created during this request.
   *
   * Creation flow:
   * 1. isUserUpload → always navigate to QMS
   * 2. Has UNAPPROVED/LIVE status → navigate to QMS (DocViewer)
   * 3. Custom (template-less) Tiptap doc → navigate to the QMS Tiptap editor
   * 4. Templated DRAFT (or no version yet) → create a version if needed, then:
   *    - has data keys → MultiStepForm (Template Assistant) to collect them
   *    - no data keys → navigate to QMS (DocViewer) with the draft
   */
  const _navigateToDoc = async (document: Document) => {
    // isUserUpload docs are always handled in the QMS (file upload, viewing, etc.)
    if (document.userUpload) {
      return navigateToQMS(document.id);
    }

    // Revision-backed drafts are shown as LIVE on the roadmap, so they must
    // open in the same DocViewer as every other LIVE document.
    let latestVersion = getLatestDocumentVersion(document);
    if (
      latestVersion &&
      (!isDraft(latestVersion) || hasDocumentRevision(document))
    ) {
      return navigateToQMS(
        document.id,
        latestVersion.id,
        latestVersion.tiptapVersion
      );
    }

    // DRAFT or no version — ensure a version exists, then either send the user
    // to QMS or to the Template Assistant.
    if (document.versions.length === 0) {
      latestVersion = await createDocumentVersionMutation.mutateAsync({
        docId: document.id,
      });
    }

    if (!latestVersion) {
      return throwNavigationError("Client Error: Document not returned.");
    }

    // Custom (template-less) Tiptap-backed documents edit the seeded content
    // directly in the QMS Tiptap editor — there are no data keys to collect.
    // Templated documents fall through to the legacy flow below: a draft with
    // data keys opens the MultiStepForm, otherwise it opens in the DocViewer.
    if (document.tiptapPath && !document.templateKey) {
      return navigateToQMS(
        document.id,
        latestVersion.id,
        latestVersion.tiptapVersion
      );
    }

    // No data keys to answer — open the draft in QMS instead of opening the
    // Template Assistant with nothing to do.
    if (!latestVersion.dataKeys?.length) {
      return navigateToQMS(
        document.id,
        latestVersion.id,
        latestVersion.tiptapVersion
      );
    }

    // Navigate to template assistant
    const searchParams = new URLSearchParams();
    searchParams.set("v", latestVersion.id);
    navigateWithPending(
      {
        pathname: fillRouteWithSlugs(ROUTES.DOCUMENT_ASSISTANT, {
          [ROUTE_SLUGS.ORG_ID]: orgId,
          [ROUTE_SLUGS.DEVICE_ID]: deviceId,
          [ROUTE_SLUGS.DOC_ID]: document.id,
        }),
        search: `?${searchParams.toString()}`,
      },
      {
        replace: true,
        state: { from: currentPath },
      }
    );
  };

  /**
   * Central navigation logic for a document (see DOCUMENT_FLOW.md § Roadmap).
   * Existing documents open in QMS. A document created during this navigation
   * opens the Template Assistant only when its first version has questions.
   */
  const navigateToWizardOrQMS = async ({
    templateKey,
    createNewMultiInstanceDoc = false,
    documentId,
  }: {
    templateKey: string;
    createNewMultiInstanceDoc?: boolean;
    documentId?: string;
  }) => {
    setGlobalPending(true);
    try {
      const { createdDocument, document } = await _resolveNavigationDocument(
        documentId,
        templateKey,
        createNewMultiInstanceDoc
      );

      if (!createdDocument) {
        const latestVersion = getLatestDocumentVersion(document);
        return navigateToQMSDocument({
          documentId: document.id,
          tiptapVersion: latestVersion?.tiptapVersion,
          versionId: latestVersion?.id,
        });
      }

      await _navigateToDoc(document);
    } finally {
      setGlobalPending(false);
    }
  };

  return {
    navigateToQMSDocument,
    navigateToWizardOrQMS,
    pending,
    _getOrCreateDocument,
    _navigateToDoc,
  };
};
